import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { cadastralApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw, MapPin, Calendar, FileText, Building } from "lucide-react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import type { CadastralInfo, DetailedCadastralInfo } from "@/types/api-v2";

interface CadastralSectionProps {
  objectId: number;
}

/** Честные тексты miss по raw_data.reason (не путать с «не на учёте»). */
function cadastralMissCopy(reason: string): { title: string; description: string } {
  if (reason.includes("no_cad_num_and_no_geometry")) {
    return {
      title: "Нет данных для поиска",
      description:
        "Нет кадастрового номера в атрибутах и нет геометрий у объекта. Добавьте контур на карте или укажите номер.",
    };
  }
  if (reason.includes("public_spatial_error")) {
    return {
      title: "Публичный spatial недоступен",
      description:
        "Архив недоступен или не дал результата, а публичный spatial НСПД вернул ошибку. Повторите позже или проверьте настройки интеграции rosreestr.",
    };
  }
  if (reason.includes("public_spatial_unavailable")) {
    return {
      title: "Публичный spatial не настроен",
      description:
        "Не удалось вызвать публичный поиск по геометрии (интеграция rosreestr / НСПД). Проверьте настройки в админке.",
    };
  }
  if (reason.includes("archive_unavailable")) {
    return {
      title: "Архив недоступен",
      description:
        "Архивная БД cadastre недоступна, а публичный путь по режиму источника не применился. Проверьте подключение архива или source_mode.",
    };
  }
  if (reason.includes("not_found_by_geometry")) {
    return {
      title: "Участок по геометрии не найден",
      description:
        "Источники ответили без ошибки пути, но участок по контуру объекта не найден. Уточните геометрию или укажите кадастровый номер.",
    };
  }
  if (reason.includes("not_found")) {
    return {
      title: "Участок не найден",
      description: "По указанному кадастровому номеру источник не вернул участок.",
    };
  }
  return {
    title: "Кадастровые данные не найдены",
    description:
      "Не удалось получить кадастровую информацию. Повторите запрос после проверки API или архива.",
  };
}

export default function CadastralSection({ objectId }: CadastralSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: cadastralInfo, isLoading, error } = useQuery<CadastralInfo | null>({
    queryKey: ["objects", objectId, "cadastral"],
    queryFn: async () => {
      try {
        return await cadastralApi.getInfo(objectId);
      } catch (err: unknown) {
        const e = err as { response?: { status?: number }; status?: number; message?: string };
        const status = e?.response?.status || e?.status;
        const message = e?.message || String(err);
        if (
          status === 404 ||
          message?.includes("404") ||
          message?.includes("Not Found") ||
          message?.includes("не найдена")
        ) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
  });

  const { data: detailedCadastralInfo, isLoading: isDetailedLoading } = useQuery<DetailedCadastralInfo | null>({
    queryKey: ["objects", objectId, "cadastral", "detailed"],
    queryFn: async () => {
      try {
        return await cadastralApi.getDetailedInfo(objectId);
      } catch (err: unknown) {
        const e = err as { response?: { status?: number }; status?: number; message?: string };
        const status = e?.response?.status || e?.status;
        const message = e?.message || String(err);
        if (
          status === 404 ||
          message?.includes("404") ||
          message?.includes("Not Found") ||
          message?.includes("не найдена")
        ) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    enabled: !!(cadastralInfo?.cadastralNumber && cadastralInfo.cadastralNumber !== "Не найден"),
  });

  const fetchCadastralMutation = useMutation({
    mutationFn: async () => {
      return await cadastralApi.fetchInfo(objectId);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["objects", objectId, "cadastral"] });
      const sourceRaw = String(data.rawData?.source || "");
      const reason = String(data.rawData?.reason || data.rawData?.resolve_method || "");
      const sourceHint =
        sourceRaw.includes("archive")
          ? "Источник: архивная БД cadastre"
          : sourceRaw.includes("api_degraded")
            ? "Источник: публичный API (архив недоступен)"
            : sourceRaw.includes("api")
              ? "Источник: публичный API / реестр"
              : null;
      const byGeometry =
        reason.includes("by_geometry") || String(data.rawData?.resolve_method || "") === "by_geometry";
      if (data.cadastralNumber === "Не найден") {
        const miss = cadastralMissCopy(reason);
        const description = sourceHint ? `${miss.description} ${sourceHint}` : miss.description;
        toast({
          title: miss.title,
          description,
          variant: reason.includes("public_spatial") || reason.includes("archive_unavailable")
            ? "destructive"
            : "default",
        });
      } else {
        toast({
          title: "Кадастровые данные обновлены",
          description: [
            byGeometry
              ? `Найден участок ${data.cadastralNumber} по геометрии объекта`
              : `Номер: ${data.cadastralNumber}`,
            sourceHint,
          ]
            .filter(Boolean)
            .join(". "),
        });
      }
    },
    onError: (error: unknown) => {
      console.error("Error fetching cadastral data:", error);
      toast({
        title: "Ошибка получения данных",
        description: (error as { message?: string })?.message || "Не удалось получить кадастровые данные",
        variant: "destructive",
      });
    },
  });

  const fetchDetailedCadastralMutation = useMutation({
    mutationFn: async () => {
      return await cadastralApi.fetchDetailedInfo(objectId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["objects", objectId, "cadastral", "detailed"] });
      toast({
        title: "Детальные кадастровые данные получены",
        description: "Расширенная информация о кадастровой стоимости и правах собственности загружена",
      });
    },
    onError: (error: unknown) => {
      console.error("Error fetching detailed cadastral data:", error);
      const message = String((error as { message?: string })?.message || "");
      if (message.includes("503")) {
        toast({
          title: "Внешние кадастровые сервисы недоступны",
          description:
            "Сервисы Росреестра временно недоступны. Проверьте настройки API в админ-панели или повторите попытку позже.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Ошибка получения детальных данных",
          description: message || "Не удалось получить детальную кадастровую информацию",
          variant: "destructive",
        });
      }
    },
  });

  const missReason = String(
    (cadastralInfo?.rawData as { reason?: string; resolve_method?: string } | undefined)?.reason ||
      (cadastralInfo?.rawData as { resolve_method?: string } | undefined)?.resolve_method ||
      "",
  );
  const missCopy = cadastralMissCopy(missReason);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Кадастровая информация</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchCadastralMutation.mutate()}
          disabled={fetchCadastralMutation.isPending}
        >
          {fetchCadastralMutation.isPending ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {cadastralInfo ? "Обновить" : "Получить данные"}
        </Button>
      </div>

      {error && !cadastralInfo ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">Кадастровые данные недоступны</p>
              <p className="text-sm">
                Нажмите &quot;Получить данные&quot; для загрузки информации из кадастрового реестра
              </p>
            </div>
          </CardContent>
        </Card>
      ) : cadastralInfo ? (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-base">
                  {cadastralInfo.cadastralNumber && cadastralInfo.cadastralNumber !== "Не найден" ? (
                    <span className="flex items-center">
                      <Building className="h-4 w-4 mr-2" />
                      {cadastralInfo.cadastralNumber}
                    </span>
                  ) : (
                    <span className="flex items-center text-gray-500">
                      <Building className="h-4 w-4 mr-2" />
                      {missCopy.title}
                    </span>
                  )}
                </CardTitle>
                {cadastralInfo.cadastralNumber === "Не найден" ? (
                  <CardDescription className="mt-1">{missCopy.description}</CardDescription>
                ) : null}
                {cadastralInfo.address && (
                  <CardDescription className="flex items-center mt-1">
                    <MapPin className="h-3 w-3 mr-1" />
                    {cadastralInfo.address}
                  </CardDescription>
                )}
              </div>
              {cadastralInfo.lastUpdated && (
                <Badge variant="secondary" className="text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  {format(new Date(cadastralInfo.lastUpdated), "dd.MM.yyyy", { locale: ru })}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cadastralInfo.area && (
                <div>
                  <div className="text-sm font-medium text-gray-600">Площадь</div>
                  <div className="text-sm">{cadastralInfo.area} м²</div>
                </div>
              )}
              {cadastralInfo.category && (
                <div>
                  <div className="text-sm font-medium text-gray-600">Категория</div>
                  <div className="text-sm">{cadastralInfo.category}</div>
                </div>
              )}
              {cadastralInfo.purpose && (
                <div className="md:col-span-2">
                  <div className="text-sm font-medium text-gray-600">Назначение</div>
                  <div className="text-sm">{cadastralInfo.purpose}</div>
                </div>
              )}
            </div>

            {cadastralInfo.coordinates && (
              <div>
                <div className="text-sm font-medium text-gray-600 mb-2">Координаты</div>
                <div className="text-xs bg-gray-50 p-2 rounded">
                  <div>Широта: {cadastralInfo.coordinates.latitude.toFixed(6)}</div>
                  <div>Долгота: {cadastralInfo.coordinates.longitude.toFixed(6)}</div>
                </div>
              </div>
            )}

            {cadastralInfo.cadastralNumber && cadastralInfo.cadastralNumber !== "Не найден" && (
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchDetailedCadastralMutation.mutate()}
                  disabled={fetchDetailedCadastralMutation.isPending}
                  className="w-full"
                >
                  {fetchDetailedCadastralMutation.isPending ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  {detailedCadastralInfo ? "Обновить детальную информацию" : "Получить детальную информацию"}
                </Button>
              </div>
            )}

            {isDetailedLoading && (
              <div className="space-y-2 pt-4 border-t">
                <div className="text-sm font-medium text-gray-600">Загрузка детальной информации...</div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            )}

            {detailedCadastralInfo && (
              <div className="pt-4 border-t space-y-4">
                <div className="text-sm font-medium text-gray-700">Детальная информация</div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {detailedCadastralInfo.cadastralCost && (
                    <div>
                      <div className="text-sm font-medium text-gray-600">Кадастровая стоимость</div>
                      <div className="text-sm font-semibold text-green-600">
                        {new Intl.NumberFormat("ru-RU", {
                          style: "currency",
                          currency: "RUB",
                          minimumFractionDigits: 0,
                        }).format(detailedCadastralInfo.cadastralCost)}
                      </div>
                    </div>
                  )}

                  {detailedCadastralInfo.costDate && (
                    <div>
                      <div className="text-sm font-medium text-gray-600">Дата оценки</div>
                      <div className="text-sm">{detailedCadastralInfo.costDate}</div>
                    </div>
                  )}

                  {detailedCadastralInfo.ownershipType && (
                    <div>
                      <div className="text-sm font-medium text-gray-600">Тип собственности</div>
                      <div className="text-sm">{detailedCadastralInfo.ownershipType}</div>
                    </div>
                  )}

                  {detailedCadastralInfo.rightType && (
                    <div>
                      <div className="text-sm font-medium text-gray-600">Вид права</div>
                      <div className="text-sm">{detailedCadastralInfo.rightType}</div>
                    </div>
                  )}

                  {detailedCadastralInfo.registrationDate && (
                    <div>
                      <div className="text-sm font-medium text-gray-600">Дата регистрации</div>
                      <div className="text-sm">{detailedCadastralInfo.registrationDate}</div>
                    </div>
                  )}

                  {detailedCadastralInfo.status && (
                    <div>
                      <div className="text-sm font-medium text-gray-600">Статус</div>
                      <Badge variant={detailedCadastralInfo.status === "Учтенный" ? "default" : "secondary"}>
                        {detailedCadastralInfo.status}
                      </Badge>
                    </div>
                  )}

                  {detailedCadastralInfo.landRecordType && (
                    <div>
                      <div className="text-sm font-medium text-gray-600">Тип записи</div>
                      <div className="text-sm">{detailedCadastralInfo.landRecordType}</div>
                    </div>
                  )}

                  {detailedCadastralInfo.landRecordSubtype && (
                    <div>
                      <div className="text-sm font-medium text-gray-600">Подтип записи</div>
                      <div className="text-sm">{detailedCadastralInfo.landRecordSubtype}</div>
                    </div>
                  )}
                </div>

                {detailedCadastralInfo.lastUpdated && (
                  <div className="text-xs text-gray-500 pt-2 border-t">
                    Детальная информация обновлена:{" "}
                    {format(new Date(detailedCadastralInfo.lastUpdated), "dd.MM.yyyy HH:mm", { locale: ru })}
                  </div>
                )}
              </div>
            )}

            {cadastralInfo.rawData && Object.keys(cadastralInfo.rawData).length > 0 && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-sm p-0 h-auto font-normal"
                >
                  {isExpanded ? "Скрыть" : "Показать"} дополнительную информацию
                </Button>
                {isExpanded && (
                  <div className="mt-2 text-xs bg-gray-50 p-3 rounded max-h-40 overflow-y-auto">
                    <pre className="whitespace-pre-wrap">{JSON.stringify(cadastralInfo.rawData, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">Кадастровые данные отсутствуют</p>
              <p className="text-sm">
                Нажмите &quot;Получить данные&quot; для загрузки информации из кадастрового реестра
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
